import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'

interface ScheduledReport {
  id: string
  name: string
  description?: string
  reportType: string
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY'
  startDate: string
  endDate?: string
  nextExecutionDate: string
  isActive: boolean
  periodType: string
  periodOffset: number
  filters?: {
    accountIds?: string[]
    costCenterIds?: string[]
  }
  emailRecipients: string[]
  exportFormat: string
  executionCount: number
  lastExecutedAt?: string
  creator: {
    firstName: string
    lastName: string
  }
  generatedReports?: Array<{
    id: string
    reportType: string
    periodStart: string
    periodEnd: string
    generatedAt: string
  }>
}

interface Account {
  id: string
  code: string
  name: string
}

interface CostCenter {
  id: string
  name: string
}

const ScheduledReports: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [reports, setReports] = useState<ScheduledReport[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [costCenters, setCostCenters] = useState<CostCenter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedReport, setSelectedReport] = useState<ScheduledReport | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    reportType: 'PROFIT_LOSS',
    frequency: 'MONTHLY' as 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    periodType: 'LAST_MONTH',
    periodOffset: 0,
    accountIds: [] as string[],
    costCenterIds: [] as string[],
    emailRecipients: [] as string[],
    emailRecipientInput: '',
    exportFormat: 'PDF',
  })

  useEffect(() => {
    fetchReports()
    fetchAccounts()
    fetchCostCenters()
  }, [])

  const fetchReports = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/scheduled-reports')
      setReports(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching scheduled reports:', error)
      toast.error('Failed to load scheduled reports')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true' } })
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const fetchCostCenters = async () => {
    try {
      const response = await axios.get('/api/erp/cost-centers')
      setCostCenters(response.data.data || [])
    } catch (error) {
      console.error('Error fetching cost centers:', error)
    }
  }

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required')
      return
    }

    if (formData.emailRecipients.length === 0) {
      toast.error('At least one email recipient is required')
      return
    }

    try {
      const payload: any = {
        name: formData.name,
        description: formData.description,
        reportType: formData.reportType,
        frequency: formData.frequency,
        startDate: new Date(formData.startDate),
        periodType: formData.periodType,
        periodOffset: formData.periodOffset,
        emailRecipients: formData.emailRecipients,
        exportFormat: formData.exportFormat,
      }

      if (formData.endDate) {
        payload.endDate = new Date(formData.endDate)
      }

      if (formData.accountIds.length > 0 || formData.costCenterIds.length > 0) {
        payload.filters = {}
        if (formData.accountIds.length > 0) {
          payload.filters.accountIds = formData.accountIds
        }
        if (formData.costCenterIds.length > 0) {
          payload.filters.costCenterIds = formData.costCenterIds
        }
      }

      await axios.post('/api/erp/scheduled-reports', payload)
      toast.success('Scheduled report created successfully')
      setShowCreateModal(false)
      resetForm()
      fetchReports()
    } catch (error: any) {
      console.error('Error creating scheduled report:', error)
      toast.error(error.response?.data?.error || 'Failed to create scheduled report')
    }
  }

  const handleView = async (id: string) => {
    try {
      const response = await axios.get(`/api/erp/scheduled-reports/${id}`)
      setSelectedReport(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching scheduled report:', error)
      toast.error('Failed to load scheduled report')
    }
  }

  const handlePause = async (id: string) => {
    try {
      await axios.post(`/api/erp/scheduled-reports/${id}/pause`)
      toast.success('Scheduled report paused')
      fetchReports()
    } catch (error: any) {
      console.error('Error pausing scheduled report:', error)
      toast.error(error.response?.data?.error || 'Failed to pause scheduled report')
    }
  }

  const handleResume = async (id: string) => {
    try {
      await axios.post(`/api/erp/scheduled-reports/${id}/resume`)
      toast.success('Scheduled report resumed')
      fetchReports()
    } catch (error: any) {
      console.error('Error resuming scheduled report:', error)
      toast.error(error.response?.data?.error || 'Failed to resume scheduled report')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this scheduled report?')) {
      return
    }

    try {
      await axios.delete(`/api/erp/scheduled-reports/${id}`)
      toast.success('Scheduled report deleted')
      fetchReports()
    } catch (error: any) {
      console.error('Error deleting scheduled report:', error)
      toast.error(error.response?.data?.error || 'Failed to delete scheduled report')
    }
  }

  const addEmailRecipient = () => {
    const email = formData.emailRecipientInput.trim()
    if (email && !formData.emailRecipients.includes(email)) {
      setFormData({
        ...formData,
        emailRecipients: [...formData.emailRecipients, email],
        emailRecipientInput: '',
      })
    }
  }

  const removeEmailRecipient = (email: string) => {
    setFormData({
      ...formData,
      emailRecipients: formData.emailRecipients.filter(e => e !== email),
    })
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      reportType: 'PROFIT_LOSS',
      frequency: 'MONTHLY',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      periodType: 'LAST_MONTH',
      periodOffset: 0,
      accountIds: [],
      costCenterIds: [],
      emailRecipients: [],
      emailRecipientInput: '',
      exportFormat: 'PDF',
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading permissions...</div>
  }

  if (!hasPermission('erp_financial_report', 'read')) {
    return <div className="p-6">You don't have permission to view scheduled reports.</div>
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Scheduled Reports</h1>
          <PageHelp pageIdentifier="scheduled-reports" title="Scheduled Reports" />
        </div>
        {hasPermission('erp_financial_report', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Scheduled Report
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading scheduled reports...</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No scheduled reports found.</p>
          {hasPermission('erp_financial_report', 'write') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First Scheduled Report
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Execution</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Executions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{report.name}</div>
                    {report.description && (
                      <div className="text-sm text-gray-500">{report.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{report.reportType.replace(/_/g, ' ')}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{report.frequency}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDateTime(report.nextExecutionDate)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      report.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {report.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {report.executionCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleView(report.id)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View
                    </button>
                    {hasPermission('erp_financial_report', 'write') && (
                      <>
                        {report.isActive ? (
                          <button
                            onClick={() => handlePause(report.id)}
                            className="text-yellow-600 hover:text-yellow-900 mr-4"
                          >
                            Pause
                          </button>
                        ) : (
                          <button
                            onClick={() => handleResume(report.id)}
                            className="text-green-600 hover:text-green-900 mr-4"
                          >
                            Resume
                          </button>
                        )}
                        {hasPermission('erp_financial_report', 'delete') && (
                          <button
                            onClick={() => handleDelete(report.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create Scheduled Report</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Monthly P&L Report"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Report Type <span className="text-red-500">*</span>
                  <FieldHelp content="Select the type of financial report to generate automatically" />
                </label>
                <select
                  value={formData.reportType}
                  onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="PROFIT_LOSS">Profit & Loss</option>
                  <option value="BALANCE_SHEET">Balance Sheet</option>
                  <option value="TRIAL_BALANCE">Trial Balance</option>
                  <option value="GENERAL_LEDGER">General Ledger</option>
                  <option value="ACCOUNT_STATEMENT">Account Statement</option>
                  <option value="AR_AGING">AR Aging</option>
                  <option value="AP_AGING">AP Aging</option>
                  <option value="COST_CENTER">Cost Center Report</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Frequency <span className="text-red-500">*</span>
                  <FieldHelp content="How often should this report be generated? Daily, weekly, monthly, quarterly, or yearly." />
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period Type <span className="text-red-500">*</span>
                  <FieldHelp content="What time period should the report cover? Last Month, Last Quarter, Last Year, Year to Date, or Custom." />
                </label>
                <select
                  value={formData.periodType}
                  onChange={(e) => setFormData({ ...formData, periodType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="LAST_MONTH">Last Month</option>
                  <option value="LAST_QUARTER">Last Quarter</option>
                  <option value="LAST_YEAR">Last Year</option>
                  <option value="YTD">Year to Date</option>
                  <option value="CUSTOM">Custom</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Recipients <span className="text-red-500">*</span>
                  <FieldHelp content="Email addresses that will receive the generated report. Add multiple recipients by entering each email and clicking Add." />
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="email"
                    value={formData.emailRecipientInput}
                    onChange={(e) => setFormData({ ...formData, emailRecipientInput: e.target.value })}
                    onKeyPress={(e) => e.key === 'Enter' && addEmailRecipient()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="email@example.com"
                  />
                  <button
                    onClick={addEmailRecipient}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    Add
                  </button>
                </div>
                {formData.emailRecipients.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.emailRecipients.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {email}
                        <button
                          onClick={() => removeEmailRecipient(email)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Export Format <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.exportFormat}
                  onChange={(e) => setFormData({ ...formData, exportFormat: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="PDF">PDF</option>
                  <option value="CSV">CSV</option>
                  <option value="EXCEL">Excel</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetForm()
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{selectedReport.name}</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Report Type</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedReport.reportType.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Frequency</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedReport.frequency}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedReport.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedReport.isActive ? 'Active' : 'Paused'}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Next Execution</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDateTime(selectedReport.nextExecutionDate)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Executions</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedReport.executionCount}</p>
                </div>
                {selectedReport.lastExecutedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Last Executed</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDateTime(selectedReport.lastExecutedAt)}</p>
                  </div>
                )}
              </div>

              {selectedReport.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Description</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedReport.description}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-500">Email Recipients</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedReport.emailRecipients.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {email}
                    </span>
                  ))}
                </div>
              </div>

              {selectedReport.generatedReports && selectedReport.generatedReports.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Recent Generated Reports</label>
                  <div className="space-y-2">
                    {selectedReport.generatedReports.map((report) => (
                      <div key={report.id} className="p-3 bg-gray-50 rounded">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">{report.reportType.replace(/_/g, ' ')}</span>
                          <span className="text-sm text-gray-500">{formatDateTime(report.generatedAt)}</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {formatDate(report.periodStart)} - {formatDate(report.periodEnd)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ScheduledReports
